import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import {
  Bus, Loader2, Pause, Play, RefreshCw, Search, X, Map as MapIcon, List, MapPin,
  Phone, MessageSquare, ArrowRight, Locate, Crosshair, ShieldAlert, Check,
  UserCheck, EyeOff, Route as RouteIcon, ChevronRight, History, Rewind, FastForward,
  Calendar, AlertCircle, Circle, CheckCircle2, Filter, BarChart3, TrendingDown,
  Flame, Trophy, Users, Activity, ChevronLeft, Maximize2, Minimize2,
} from "lucide-react";

/**
 * Suivi Live — Phase 2A
 * =====================
 * Centre opérationnel temps réel. Layout :
 *
 *   ┌─ Header (4 KPIs · search · view toggle · replay · pause/refresh) ────┐
 *   ├──────────────┬───────────────────────────────────────┬──────────────┤
 *   │ Lines panel  │  Map (75-80% width) with GTFS         │ Context     │
 *   │ - by line    │  - vehicles markers (#, driver, sta.) │ panel       │
 *   │   ▸ buses    │  - GTFS line polylines (colored)      │ - default:  │
 *   │   ▸ inc.     │  - stops + schools (different glyph)  │   alerts    │
 *   │   ▸ delays   │  - incidents pins                     │ - bus/line/ │
 *   │   ▸ ponctu.  │  - quick filters bar                  │   stop      │
 *   └──────────────┴───────────────────────────────────────┴──────────────┘
 *
 * Phase 2A adds:
 *   • Vertical timeline of the active trip inside the bus panel.
 *   • Replay mode: scrub through any past day with 1x..20x speeds,
 *     incidents + delays + stops effectués pinned on the slider.
 *   • Exploitation table with Incidents, Tournée columns + driver/status
 *     filters and full bidirectional map sync.
 */

const REFRESH_MS = 10000;
const REPLAY_SPEEDS = [1, 2, 5, 10, 20];

/* Fixed line palette per the spec: 701 yellow, 702 blue, 704 green, 724 red,
 * 730 orange, 760 violet, R57 dark blue. */
const LINE_COLORS = {
  "701": "#F59E0B", "702": "#3B82F6", "704": "#10B981", "724": "#DC2626",
  "730": "#EA580C", "760": "#8B5CF6", "R57": "#1E40AF",
};
const LINE_FALLBACK_PALETTE = ["#0EA5E9", "#A855F7", "#14B8A6", "#F472B6", "#6366F1", "#EAB308"];
function lineColor(rn) {
  if (LINE_COLORS[rn]) return LINE_COLORS[rn];
  const h = (rn || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return LINE_FALLBACK_PALETTE[h % LINE_FALLBACK_PALETTE.length];
}

const BUS_STATE = {
  CRITICAL:        { label: "Critique",   color: "#DC2626" },
  DELAY_IMPORTANT: { label: "Retard ++",  color: "#EA580C" },
  LIGHT_DELAY:     { label: "Retard",     color: "#F59E0B" },
  ON_TIME:         { label: "À l'heure",  color: "#10B981" },
  IDLE:            { label: "Stationné",  color: "#3B82F6" },
  OFFLINE:         { label: "Hors ligne", color: "#71717A" },
  NO_GPS:          { label: "GPS absent", color: "#9333EA" },
};

/* Vehicle marker — driver name visible at zoom ≥ 13. ----------------------*/
function busMarker(v, { selected = false, zoom = 11, ghosted = false } = {}) {
  const meta = BUS_STATE[(v.status || "").toUpperCase()] || BUS_STATE.OFFLINE;
  const ring = selected ? "0 0 0 3px #FFC60A," : "";
  const delay = Number(v.forced_delay_minutes ?? 0);
  const tag = v.label || v.license_plate || v.vehicle_id || "?";
  const showDriver = zoom >= 13 && v.driver_name;
  const opacity = ghosted ? 0.45 : 1;
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center;font-family:'Space Grotesk',sans-serif;opacity:${opacity};">
      <div style="background:${meta.color};color:#fff;font-weight:900;font-size:11px;padding:3px 6px;border:2px solid #0A0A0B;box-shadow:${ring}2px 2px 0 0 #0A0A0B;white-space:nowrap;line-height:1;">${tag}</div>
      ${delay >= 3 ? `<div style="background:${meta.color};color:#fff;font-weight:700;font-size:9px;padding:1px 4px;border:2px solid #0A0A0B;border-top:0;line-height:1;">+${delay}m</div>` : ""}
      ${showDriver ? `<div style="background:#0A0A0B;color:#FFC60A;font-size:9px;font-weight:600;padding:1px 4px;border:2px solid #0A0A0B;border-top:0;line-height:1;white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis;">${v.driver_name}</div>` : ""}
    </div>`,
    iconSize: [60, 50], iconAnchor: [30, 15],
  });
}

function replayBusMarker({ tag, color, selected = false }) {
  const ring = selected ? "0 0 0 3px #FFC60A," : "";
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;font-weight:900;font-size:11px;padding:3px 6px;border:2px solid #0A0A0B;box-shadow:${ring}2px 2px 0 0 #0A0A0B;white-space:nowrap;line-height:1;font-family:'Space Grotesk',sans-serif;">${tag}</div>`,
    iconSize: [60, 22], iconAnchor: [30, 11],
  });
}

function stopIcon(s) {
  const isSchool = s.type === "school";
  if (isSchool) {
    return L.divIcon({
      className: "",
      html: `<div style="width:22px;height:22px;background:#F59E0B;border:2px solid #0A0A0B;display:flex;align-items:center;justify-content:center;font-size:13px;line-height:1;">🏫</div>`,
      iconSize: [22, 22], iconAnchor: [11, 11],
    });
  }
  return L.divIcon({
    className: "",
    html: `<div style="width:12px;height:12px;background:#fff;border:2px solid #0A0A0B;border-radius:999px;"></div>`,
    iconSize: [12, 12], iconAnchor: [6, 6],
  });
}

function incidentIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;background:#DC2626;color:#fff;border:2px solid #0A0A0B;border-radius:999px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;line-height:1;">!</div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
  });
}

function delayEventIcon(delay) {
  const color = delay >= 7 ? "#DC2626" : delay >= 3 ? "#F59E0B" : "#10B981";
  return L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;background:${color};border:2px solid #0A0A0B;border-radius:999px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:8px;font-weight:900;line-height:1;">${delay > 0 ? "+" : ""}</div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
}

/* Map helpers ------------------------------------------------------------- */
function ZoomReporter({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
}
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lon], target.zoom || 14, { duration: 0.6 }); }, [map, target]);
  return null;
}
function FollowVehicle({ vehicle, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !vehicle?.lat || !vehicle?.lon) return;
    map.panTo([vehicle.lat, vehicle.lon], { animate: true, duration: 0.4 });
  }, [enabled, vehicle, map]);
  return null;
}
function CenterFleet({ trigger, vehicles }) {
  const map = useMap();
  useEffect(() => {
    if (!trigger) return;
    const pts = vehicles.filter((v) => v.lat != null && v.lon != null).map((v) => [v.lat, v.lon]);
    if (pts.length === 0) return;
    if (pts.length === 1) { map.setView(pts[0], 13); return; }
    map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 13 });
  }, [trigger, vehicles, map]);
  return null;
}

/* ── Main page wrapper with 3 tabs ───────────────────────────────────────
 * LIVE       → Cockpit temps réel (carte, panneaux, exploitation)
 * REPLAY     → Rejeu d'une journée passée, plein écran
 * ANALYTIQUE → KPIs historiques, hotspots, incidents par dimension
 * ─────────────────────────────────────────────────────────────────────── */
export default function LiveTracking() {
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "live";
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["live", "replay", "analytics"].includes(t) ? t : "live";
  });
  // Persist tab in URL so deep-links work and tab survives refresh
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (tab === "live") url.searchParams.delete("tab");
    else url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }, [tab]);
  return (
    <div className="flex flex-col h-[calc(100vh-0px)] bg-zinc-100" data-testid="saeiv-page">
      <SaeivTabs tab={tab} setTab={setTab} />
      {tab === "live" && <LiveCockpit key="live" forceReplay={false} />}
      {tab === "replay" && <LiveCockpit key="replay" forceReplay={true} />}
      {tab === "analytics" && <AnalyticsTab key="analytics" />}
    </div>
  );
}

function SaeivTabs({ tab, setTab }) {
  const TABS = [
    { id: "live", label: "Live", desc: "Temps réel" },
    { id: "replay", label: "Replay", desc: "Rejeu d'une journée" },
    { id: "analytics", label: "Analytique", desc: "KPIs & hotspots" },
  ];
  return (
    <div className="bg-zinc-900 border-b border-zinc-800 flex items-stretch px-3 flex-shrink-0" data-testid="saeiv-tabs">
      <div className="flex items-center pr-4 border-r border-zinc-800">
        <div className="text-[9px] font-mono tracking-[0.3em] uppercase text-[#FFC60A]">CENTRE D'EXPLOITATION</div>
      </div>
      {TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`saeiv-tab-${t.id}`}
            className={`px-4 py-2 text-left border-r border-zinc-800 flex flex-col justify-center min-w-[120px] transition ${active ? "bg-[#FFC60A] text-zinc-900" : "text-zinc-300 hover:bg-zinc-800"}`}>
            <span className="font-display font-black text-base tracking-tighter leading-none">{t.label}</span>
            <span className={`text-[9px] font-mono uppercase tracking-widest mt-0.5 ${active ? "text-zinc-800" : "text-zinc-500"}`}>{t.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── LIVE Cockpit (also used as the REPLAY surface when forceReplay=true) ─ */
function LiveCockpit({ forceReplay = false }) {
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [linesSummary, setLinesSummary] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [stops, setStops] = useState([]);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("map");
  // Filters
  const [showVehicles, setShowVehicles] = useState(true);
  const [showLines, setShowLines] = useState(true);
  const [showStops, setShowStops] = useState(true);
  const [showSchools, setShowSchools] = useState(true);
  const [showIncidents, setShowIncidents] = useState(true);
  const [delayOnly, setDelayOnly] = useState(false);
  const [routeFilter, setRouteFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [incidentsOnly, setIncidentsOnly] = useState(false);
  // Selection
  const [sel, setSel] = useState({ kind: "alerts" });
  // Map control
  const [mapZoom, setMapZoom] = useState(11);
  const [flyTarget, setFlyTarget] = useState(null);
  const [centerSig, setCenterSig] = useState(0);
  const [followSelected, setFollowSelected] = useState(false);
  const initialFit = useRef(false);
  const pollTimer = useRef(null);
  // Collapsible panels (persisted in localStorage)
  const [leftOpen, setLeftOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("saeiv.leftOpen");
    return v === null ? true : v === "1";
  });
  const [rightOpen, setRightOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("saeiv.rightOpen");
    return v === null ? true : v === "1";
  });
  useEffect(() => { try { window.localStorage.setItem("saeiv.leftOpen", leftOpen ? "1" : "0"); } catch (_e) { /* noop */ } }, [leftOpen]);
  useEffect(() => { try { window.localStorage.setItem("saeiv.rightOpen", rightOpen ? "1" : "0"); } catch (_e) { /* noop */ } }, [rightOpen]);
  // Replay state
  const [replayOn, setReplayOn] = useState(forceReplay);
  useEffect(() => { setReplayOn(forceReplay); }, [forceReplay]);
  const [replayDate, setReplayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [replayPayload, setReplayPayload] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(5);
  const [replayCursor, setReplayCursor] = useState(0);   // ms since day start
  const [replayMode, setReplayMode] = useState("vehicle"); // "vehicle" | "line"
  const replayTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vres, ires, lres, sres, stres] = await Promise.all([
        api.get("/logibus/live/vehicles"),
        api.get("/incidents?status=open").catch(() => ({ data: [] })),
        api.get("/logibus/live/lines-summary").catch(() => ({ data: { lines: [] } })),
        api.get("/logibus/gtfs/shapes").catch(() => ({ data: { shapes: [] } })),
        api.get("/logibus/stops-map").catch(() => ({ data: { stops: [] } })),
      ]);
      setVehicles(vres.data?.vehicles || []);
      const inc = Array.isArray(ires.data) ? ires.data : (ires.data?.items || []);
      setIncidents(inc.filter((i) => !(i.message || "").startsWith("TEST_")));
      setLinesSummary(lres.data?.lines || []);
      setShapes(sres.data?.shapes || []);
      setStops(stres.data?.stops || []);
      setRefreshedAt(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (paused || replayOn) { if (pollTimer.current) clearInterval(pollTimer.current); return; }
    pollTimer.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(pollTimer.current);
  }, [paused, replayOn, load]);

  // Apply ?vehicle=X param on first load
  useEffect(() => {
    const vid = params.get("vehicle");
    if (vid && vehicles.length > 0 && sel.kind === "alerts") {
      const v = vehicles.find((x) => x.vehicle_id === vid);
      if (v) { setSel({ kind: "bus", vehicle_id: v.vehicle_id }); setFlyTarget({ lat: v.lat, lon: v.lon, zoom: 14 }); }
    }
  }, [params, vehicles, sel.kind]);

  // WS realtime push
  const wsDebounce = useRef(null);
  const wsRefresh = useCallback(() => {
    if (paused || replayOn) return;
    if (wsDebounce.current) clearTimeout(wsDebounce.current);
    wsDebounce.current = setTimeout(() => load(), 400);
  }, [paused, replayOn, load]);
  useRealtime(
    useMemo(() => ["live_vehicles", "alerts", "missions", "tour_assignments"], []),
    useMemo(() => ({
      "vehicle.position": wsRefresh,
      "incident.created": wsRefresh,
      "mission.updated":  wsRefresh,
      // Single-source: any assignment change refreshes the live tracking
      // map + table so the bus shows the right driver/tour right away.
      "assignment.created":        wsRefresh,
      "assignment.updated":        wsRefresh,
      "assignment.deleted":        wsRefresh,
      "assignment.moved":          wsRefresh,
      "assignment.driver_changed": wsRefresh,
      "assignment.upserted":       wsRefresh,
    }), [wsRefresh]),
  );

  // ── Replay loader ───────────────────────────────────────────────────────
  const fetchReplay = useCallback(async () => {
    setReplayLoading(true);
    try {
      const q = new URLSearchParams({ date: replayDate });
      if (replayMode === "vehicle" && sel.kind === "bus") q.set("vehicle_id", sel.vehicle_id);
      else if (replayMode === "line" && (routeFilter || (sel.kind === "line" && sel.route_short_name))) {
        q.set("route_short_name", routeFilter || sel.route_short_name);
      } else if (replayMode === "vehicle") {
        toast.warning("Sélectionnez d'abord un véhicule sur la carte ou le tableau.");
        setReplayLoading(false);
        return;
      } else {
        toast.warning("Sélectionnez d'abord une ligne (filtre ou panneau).");
        setReplayLoading(false);
        return;
      }
      const { data } = await api.get(`/logibus/live/replay?${q.toString()}`);
      setReplayPayload(data);
      setReplayCursor(0);
      setReplayPlaying(false);
      if ((data?.total_points || 0) === 0) {
        toast.info("Aucune donnée de replay pour cette journée.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setReplayLoading(false); }
  }, [replayDate, replayMode, sel, routeFilter]);

  // Replay tick
  useEffect(() => {
    if (!replayOn || !replayPlaying || !replayPayload) return;
    const start = new Date(replayPayload.range.from_iso).getTime();
    const end = new Date(replayPayload.range.to_iso).getTime();
    const span = Math.max(end - start, 60_000);
    const stepMs = 200; // tick every 200ms
    const advancePerTick = (stepMs * replaySpeed * 60); // 60s of replay = 1s wall at 1x → tweak as needed
    replayTimerRef.current = setInterval(() => {
      setReplayCursor((c) => {
        const next = c + advancePerTick;
        if (next >= span) { setReplayPlaying(false); return span; }
        return next;
      });
    }, stepMs);
    return () => clearInterval(replayTimerRef.current);
  }, [replayOn, replayPlaying, replayPayload, replaySpeed]);

  // KPI bar
  const kpis = useMemo(() => {
    const total = vehicles.length;
    const active = vehicles.filter((v) => v.lat != null && v.lon != null).length;
    const lines = new Set(vehicles.map((v) => v.route_short_name).filter(Boolean)).size || linesSummary.length;
    const delays = vehicles.filter((v) => (v.forced_delay_minutes || 0) >= 3).length;
    const criticalAlerts = incidents.filter((i) => ["critical", "high"].includes((i.severity || "").toLowerCase())).length;
    return { total, active, lines, delays, criticalAlerts };
  }, [vehicles, incidents, linesSummary]);

  // Incidents indexed by vehicle_id for the table column
  const incidentsByVehicle = useMemo(() => {
    const m = {};
    incidents.forEach((i) => {
      const vid = i.vehicle_id || (i.extra || {}).vehicle_id;
      if (!vid) return;
      m[vid] = (m[vid] || 0) + 1;
    });
    return m;
  }, [incidents]);

  // Filtered vehicles
  const filteredVehicles = useMemo(() => {
    const q = search.toLowerCase().trim();
    return vehicles.filter((v) => {
      if (!showVehicles) return false;
      if (q) {
        const hay = `${v.label || ""} ${v.license_plate || ""} ${v.driver_name || ""} ${v.route_short_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (routeFilter && (v.route_short_name || "") !== routeFilter) return false;
      if (driverFilter && (v.driver_name || "") !== driverFilter) return false;
      if (statusFilter && (v.status || "").toUpperCase() !== statusFilter) return false;
      if (delayOnly && (v.forced_delay_minutes || 0) < 3) return false;
      if (incidentsOnly && !(incidentsByVehicle[v.vehicle_id] || 0)) return false;
      return true;
    });
  }, [vehicles, search, routeFilter, driverFilter, statusFilter, delayOnly, incidentsOnly, showVehicles, incidentsByVehicle]);

  const lineOptions = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.route_short_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, "fr", { numeric: true })),
    [vehicles]
  );
  const driverOptions = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.driver_name).filter(Boolean))).sort(),
    [vehicles]
  );

  // The vehicle to follow when followSelected is enabled
  const selectedVehicle = useMemo(
    () => (sel.kind === "bus" ? vehicles.find((v) => v.vehicle_id === sel.vehicle_id) : null),
    [sel, vehicles]
  );

  // Stops to render
  const renderedStops = useMemo(() => {
    if (!showStops && !showSchools) return [];
    return stops.filter((s) => {
      if (s.type === "school" && !showSchools) return false;
      if (s.type !== "school" && !showStops) return false;
      if (mapZoom < 12 && s.type !== "school") return false;
      return true;
    });
  }, [stops, showStops, showSchools, mapZoom]);

  // Derived: replay vehicles at current cursor
  const replayVehicleStates = useMemo(() => {
    if (!replayOn || !replayPayload) return [];
    const start = new Date(replayPayload.range.from_iso).getTime();
    const tNow = start + replayCursor;
    return replayPayload.vehicles.map((rv) => {
      const pts = rv.points || [];
      let cur = null, idx = -1;
      for (let i = 0; i < pts.length; i++) {
        const ts = new Date(pts[i].timestamp).getTime();
        if (ts <= tNow) { cur = pts[i]; idx = i; } else break;
      }
      return { ...rv, cur, idx };
    }).filter((v) => v.cur);
  }, [replayOn, replayPayload, replayCursor]);
  const replayEventsBefore = useMemo(() => {
    if (!replayOn || !replayPayload) return [];
    const start = new Date(replayPayload.range.from_iso).getTime();
    const tNow = start + replayCursor;
    const out = [];
    (replayPayload.vehicles || []).forEach((rv) => {
      (rv.events || []).forEach((ev) => {
        if (new Date(ev.created_at).getTime() <= tNow && ev.lat && ev.lon) {
          out.push({ ...ev, _veh: rv.label });
        }
      });
    });
    return out;
  }, [replayOn, replayPayload, replayCursor]);
  const replayIncidentsBefore = useMemo(() => {
    if (!replayOn || !replayPayload) return [];
    const start = new Date(replayPayload.range.from_iso).getTime();
    const tNow = start + replayCursor;
    return (replayPayload.incidents || []).filter((i) => new Date(i.created_at).getTime() <= tNow);
  }, [replayOn, replayPayload, replayCursor]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-zinc-100" data-testid="suivi-live-page">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="bg-zinc-900 text-white border-b-2 border-[#FFC60A] flex items-center px-3 py-2 gap-3 flex-wrap flex-shrink-0">
        <div className="flex items-center gap-2 mr-2">
          <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">SAEIV · {replayOn ? "Mode Replay" : "Suivi temps réel"}</div>
          <div className="font-display font-black text-lg tracking-tighter">{forceReplay ? "REPLAY" : "LIVE"}</div>
        </div>
        <Kpi label="Bus en circulation"  value={kpis.active}        secondary={`/ ${kpis.total}`} accent="#10B981" testId="sl-kpi-buses" />
        <Kpi label="Lignes actives"       value={kpis.lines}        testId="sl-kpi-lines" />
        <Kpi label="Retards actifs"       value={kpis.delays}       accent={kpis.delays ? "#F59E0B" : null} onClick={() => setDelayOnly((v) => !v)} testId="sl-kpi-delays" />
        <Kpi label="Alertes critiques"    value={kpis.criticalAlerts} accent={kpis.criticalAlerts ? "#EF4444" : null} onClick={() => setSel({ kind: "alerts" })} testId="sl-kpi-alerts" />

        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button onClick={() => setView("map")} data-testid="sl-view-map"
            className={`px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${view === "map" ? "bg-[#FFC60A] text-zinc-900" : "hover:bg-zinc-800"}`}>
            <MapIcon size={11} /> Carte
          </button>
          <button onClick={() => setView("table")} data-testid="sl-view-table"
            className={`px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${view === "table" ? "bg-[#FFC60A] text-zinc-900" : "hover:bg-zinc-800"}`}>
            <List size={11} /> Tableau
          </button>
        </div>
        <div className="flex items-center border border-zinc-700 px-2 py-1 bg-zinc-800">
          <Search size={11} className="text-zinc-500 mr-1.5" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Bus, ligne, chauffeur…"
            data-testid="sl-search" className="bg-transparent outline-none text-xs w-44 text-white placeholder:text-zinc-500" />
        </div>
        <button onClick={() => { setReplayOn((v) => !v); setReplayPlaying(false); }} data-testid="sl-replay-toggle"
          className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${forceReplay ? "hidden" : ""} ${replayOn ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "border-zinc-700 hover:bg-zinc-800"}`}>
          <History size={11} /> Replay
        </button>
        {!replayOn && (
          <>
            <button onClick={() => setPaused((p) => !p)} data-testid="sl-pause"
              className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:bg-zinc-800">
              {paused ? <><Play size={11} /> Lecture</> : <><Pause size={11} /> Pause</>}
            </button>
            <button onClick={load} disabled={loading} data-testid="sl-refresh"
              className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:bg-zinc-800 disabled:opacity-50">
              {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
            </button>
          </>
        )}
        {view === "map" && (
          <button
            onClick={() => {
              const allOpen = leftOpen && rightOpen;
              setLeftOpen(!allOpen);
              setRightOpen(!allOpen);
            }}
            data-testid="sl-fullscreen"
            title={leftOpen && rightOpen ? "Plein écran carte" : "Restaurer les panneaux"}
            className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${!leftOpen && !rightOpen ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "border-zinc-700 hover:bg-zinc-800"}`}>
            {leftOpen && rightOpen ? <><Maximize2 size={11} /> Carte</> : <><Minimize2 size={11} /> Panneaux</>}
          </button>
        )}
        <div className="text-[9px] font-mono text-zinc-400 ml-1">
          maj {refreshedAt ? refreshedAt.toLocaleTimeString("fr-CH").slice(0, 5) : "—"} · {paused || replayOn ? "⏸" : `${REFRESH_MS / 1000}s`}
        </div>
      </header>

      {/* ── Replay bar ──────────────────────────────────────────────────── */}
      {replayOn && (
        <ReplayBar
          date={replayDate} setDate={setReplayDate}
          mode={replayMode} setMode={setReplayMode}
          payload={replayPayload}
          loading={replayLoading}
          playing={replayPlaying} setPlaying={setReplayPlaying}
          speed={replaySpeed} setSpeed={setReplaySpeed}
          cursor={replayCursor} setCursor={setReplayCursor}
          incidents={replayPayload?.incidents || []}
          events={replayPayload?.vehicles?.flatMap((v) => v.events || []) || []}
          onLoad={fetchReplay}
          selKind={sel.kind}
          selLine={routeFilter || (sel.kind === "line" ? sel.route_short_name : "")}
          selVeh={sel.kind === "bus" ? sel.vehicle_id : null}
        />
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {view === "map" ? (
          <>
            {/* Left: lines list (collapsible) */}
            {leftOpen ? (
              <div className="relative flex-shrink-0" data-testid="sl-left-wrap">
                <LinesPanel
                  lines={linesSummary}
                  activeRoute={routeFilter}
                  vehicles={vehicles}
                  onSelect={(rn) => {
                    setRouteFilter((p) => (p === rn ? "" : rn));
                    if (rn) setSel({ kind: "line", route_short_name: rn });
                  }}
                />
                <button onClick={() => setLeftOpen(false)} data-testid="sl-left-collapse"
                  title="Réduire le panneau des lignes"
                  className="absolute top-2 -right-3 z-[600] w-6 h-6 bg-white border border-zinc-900 shadow-[2px_2px_0_0_#0A0A0B] hover:bg-[#FFC60A] flex items-center justify-center">
                  <ChevronLeft size={12} />
                </button>
              </div>
            ) : (
              <CollapsedStrip
                side="left"
                count={linesSummary.length}
                label="Lignes"
                onOpen={() => setLeftOpen(true)}
                testId="sl-left-expand"
              />
            )}

            {/* Map */}
            <section className="flex-1 relative bg-white" data-testid="sl-map">
              {/* Quick filters toolbar */}
              <div className="absolute top-2 left-2 z-[500] bg-white border border-zinc-900 px-2 py-1 flex items-center gap-1.5 flex-wrap shadow-[2px_2px_0_0_#0A0A0B]">
                <Chip label="Bus"        active={showVehicles}    onClick={() => setShowVehicles((v) => !v)} testId="sl-f-buses" />
                <Chip label="Lignes"     active={showLines}       onClick={() => setShowLines((v) => !v)}    testId="sl-f-lines" />
                <Chip label="Arrêts"     active={showStops}       onClick={() => setShowStops((v) => !v)}    testId="sl-f-stops" />
                <Chip label="Écoles"     active={showSchools}     onClick={() => setShowSchools((v) => !v)}  testId="sl-f-schools" />
                <Chip label="Incidents"  active={showIncidents}   onClick={() => setShowIncidents((v) => !v)} testId="sl-f-incidents" />
                <Chip label="Retards"    active={delayOnly}       onClick={() => setDelayOnly((v) => !v)}    testId="sl-f-delays" />
                <div className="h-4 w-px bg-zinc-300" />
                <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} data-testid="sl-f-line"
                  className="text-xs font-mono p-1 outline-none bg-white border border-zinc-300 hover:border-zinc-900">
                  <option value="">Toutes les lignes</option>
                  {lineOptions.map((l) => <option key={l} value={l}>{`Ligne ${l}`}</option>)}
                </select>
              </div>

              {/* Map control buttons (top-right) */}
              <div className="absolute top-2 right-2 z-[500] flex flex-col gap-1">
                <button onClick={() => setCenterSig((n) => n + 1)} data-testid="sl-center-fleet"
                  title="Centrer sur ma flotte"
                  className="bg-white border border-zinc-900 p-1.5 hover:bg-[#FFC60A] shadow-[2px_2px_0_0_#0A0A0B] inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest">
                  <Crosshair size={11} /> Flotte
                </button>
                {sel.kind === "bus" && (
                  <button onClick={() => setFollowSelected((v) => !v)} data-testid="sl-follow-veh"
                    title="Suivre automatiquement le véhicule sélectionné"
                    className={`border border-zinc-900 p-1.5 shadow-[2px_2px_0_0_#0A0A0B] inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest ${followSelected ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-[#FFC60A]"}`}>
                    <Locate size={11} /> {followSelected ? "Suivi ON" : "Suivre"}
                  </button>
                )}
              </div>

              <MapContainer center={[46.51, 6.49]} zoom={11} scrollWheelZoom attributionControl={false}
                style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <ZoomReporter onZoom={setMapZoom} />
                <FlyTo target={flyTarget} />
                <FollowVehicle vehicle={selectedVehicle} enabled={followSelected && !replayOn} />
                <CenterFleet trigger={centerSig} vehicles={filteredVehicles} />
                {!initialFit.current && filteredVehicles.length > 0 && (initialFit.current = true) && (
                  <CenterFleet trigger={1} vehicles={filteredVehicles} />
                )}

                {/* GTFS shapes */}
                {showLines && shapes.filter((s) => !routeFilter || s.route_short_name === routeFilter).map((s) => (
                  <Polyline key={s.route_id} positions={s.coords} pathOptions={{ color: lineColor(s.route_short_name), weight: 4, opacity: 0.55 }}>
                    <Tooltip sticky direction="top" opacity={0.95}>
                      <strong style={{ color: lineColor(s.route_short_name) }}>Ligne {s.route_short_name}</strong>
                      {s.route_long_name && <div className="text-[10px]">{s.route_long_name}</div>}
                    </Tooltip>
                  </Polyline>
                ))}

                {/* Stops + schools */}
                {renderedStops.map((s) => (
                  <Marker key={s.stop_id} position={[s.lat, s.lon]} icon={stopIcon(s)}
                    eventHandlers={{ click: () => setSel({ kind: "stop", stop_id: s.stop_id }) }}>
                    <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                      <strong>{s.type === "school" ? `🏫 ${s.stop_name}` : s.stop_name}</strong>
                      {s.city && <div className="text-[10px]">{s.city}</div>}
                    </Tooltip>
                  </Marker>
                ))}

                {/* Live mode incidents OR replay incidents */}
                {!replayOn && showIncidents && incidents
                  .filter((i) => typeof i.lat === "number" && typeof i.lon === "number")
                  .slice(0, 40)
                  .map((i) => (
                    <Marker key={i.id} position={[i.lat, i.lon]} icon={incidentIcon()}>
                      <Popup>
                        <div className="text-xs">
                          <strong>{(i.type || "Incident").replace("_", " ")}</strong>
                          <div className="mt-0.5 text-zinc-600 text-[11px]">{i.message || "—"}</div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}

                {/* Replay: trails + incidents + delay events */}
                {replayOn && replayPayload && (
                  <>
                    {replayPayload.vehicles?.map((rv) => {
                      const start = new Date(replayPayload.range.from_iso).getTime();
                      const tNow = start + replayCursor;
                      const pts = (rv.points || []).filter((p) => new Date(p.timestamp).getTime() <= tNow);
                      const color = lineColor(rv.route_short_name);
                      if (pts.length < 2) return null;
                      return (
                        <Polyline key={`rt-${rv.vehicle_id}`} positions={pts.map((p) => [p.lat, p.lon])}
                          pathOptions={{ color, weight: 3, opacity: 0.8, dashArray: "1,6" }} />
                      );
                    })}
                    {replayEventsBefore.map((ev, i) => (
                      <Marker key={`ev-${i}-${ev.stop_id || ev.created_at}`} position={[ev.lat, ev.lon]} icon={delayEventIcon(ev.delay_minutes || 0)}>
                        <Tooltip direction="top" opacity={0.95}>
                          <div className="text-[11px]"><strong>{ev._veh}</strong> · {ev.stop_name}</div>
                          <div className="text-[10px]">{(ev.delay_minutes || 0) > 0 ? `+${ev.delay_minutes}m` : "à l'heure"} · {(ev.detected_time || "").slice(11, 16)}</div>
                        </Tooltip>
                      </Marker>
                    ))}
                    {replayIncidentsBefore.filter((i) => typeof i.lat === "number" && typeof i.lon === "number").map((i) => (
                      <Marker key={`rinc-${i.id}`} position={[i.lat, i.lon]} icon={incidentIcon()}>
                        <Tooltip direction="top" opacity={0.95}>
                          <strong>{(i.type || "Incident")}</strong>
                          <div className="text-[10px]">{(i.created_at || "").slice(11, 16)} · {i.message || ""}</div>
                        </Tooltip>
                      </Marker>
                    ))}
                  </>
                )}

                {/* Vehicle markers: live OR replay ghost */}
                {!replayOn && filteredVehicles
                  .filter((v) => typeof v.lat === "number" && typeof v.lon === "number")
                  .map((v) => (
                    <Marker key={v.vehicle_id} position={[v.lat, v.lon]}
                      icon={busMarker(v, { selected: sel.kind === "bus" && sel.vehicle_id === v.vehicle_id, zoom: mapZoom })}
                      eventHandlers={{ click: () => setSel({ kind: "bus", vehicle_id: v.vehicle_id }) }}>
                      <Tooltip direction="top" offset={[0, -22]} opacity={0.95}>
                        <div className="text-[11px]">
                          <strong>{v.label || v.license_plate || v.vehicle_id}</strong>
                          {v.route_short_name && <div>Ligne {v.route_short_name}</div>}
                          {v.driver_name && <div>{v.driver_name}</div>}
                          <div className={`mt-1 font-bold ${v.is_in_service ? "text-emerald-600" : "text-red-600"}`} data-testid="live-tooltip-service-status">
                            {v.is_in_service ? "En service" : "Hors service"}
                          </div>
                          {(v.in_service_checks || []).map((c) => (
                            <div key={c.key} className={c.ok ? "text-emerald-700" : "text-zinc-600"}>
                              {c.ok ? "✓" : "•"} {c.label}
                            </div>
                          ))}
                        </div>
                      </Tooltip>
                    </Marker>
                  ))}
                {replayOn && replayVehicleStates.map((rv) => {
                  const color = lineColor(rv.route_short_name);
                  return (
                    <Marker key={`rv-${rv.vehicle_id}`} position={[rv.cur.lat, rv.cur.lon]}
                      icon={replayBusMarker({ tag: rv.label, color, selected: sel.kind === "bus" && sel.vehicle_id === rv.vehicle_id })}
                      eventHandlers={{ click: () => setSel({ kind: "bus", vehicle_id: rv.vehicle_id }) }}>
                      <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                        <div className="text-[11px]">
                          <strong>{rv.label}</strong>
                          {rv.route_short_name && <div>Ligne {rv.route_short_name}</div>}
                          {rv.driver_name && <div>{rv.driver_name}</div>}
                          <div className="text-[10px] font-mono">{(rv.cur.timestamp || "").slice(11, 19)}</div>
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}
              </MapContainer>

              {/* Legend bottom-left */}
              <div className="absolute bottom-2 left-2 z-[500] bg-white/95 border border-zinc-900 px-2 py-1 text-[9px] font-mono flex items-center gap-2 flex-wrap shadow-[2px_2px_0_0_#0A0A0B] max-w-[700px]">
                {Object.entries(BUS_STATE).map(([k, m]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 inline-block" style={{ background: m.color }} /> {m.label}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1">| <span>🏫</span> École</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block bg-red-600 rounded-full border border-zinc-900" /> Incident</span>
              </div>
            </section>

            {/* Right contextual panel (collapsible) */}
            {rightOpen ? (
              <aside className="w-[360px] border-l border-zinc-900 bg-white flex flex-col overflow-hidden flex-shrink-0 relative" data-testid="sl-panel">
                <button onClick={() => setRightOpen(false)} data-testid="sl-right-collapse"
                  title="Réduire le panneau contextuel"
                  className="absolute top-2 -left-3 z-[600] w-6 h-6 bg-white border border-zinc-900 shadow-[2px_2px_0_0_#0A0A0B] hover:bg-[#FFC60A] flex items-center justify-center">
                  <ChevronRight size={12} />
                </button>
                <ContextPanel
                  sel={sel} vehicles={vehicles} incidents={incidents}
                  onClose={() => setSel({ kind: "alerts" })}
                  onSelect={setSel}
                  onFlyTo={setFlyTarget}
                  onRefresh={load}
                />
              </aside>
            ) : (
              <CollapsedStrip
                side="right"
                count={incidents.length}
                label="Alertes"
                tone={incidents.length > 0 ? "danger" : "neutral"}
                onOpen={() => setRightOpen(true)}
                testId="sl-right-expand"
              />
            )}
          </>
        ) : (
          <ExploitationTable
            vehicles={filteredVehicles}
            sel={sel}
            onSelect={(v) => { setSel({ kind: "bus", vehicle_id: v.vehicle_id }); setFlyTarget({ lat: v.lat, lon: v.lon, zoom: 14 }); }}
            onSelectAndShowMap={(v) => { setView("map"); setSel({ kind: "bus", vehicle_id: v.vehicle_id }); setRightOpen(true); setFlyTarget({ lat: v.lat, lon: v.lon, zoom: 14 }); }}
            incidentsByVehicle={incidentsByVehicle}
            routeFilter={routeFilter} setRouteFilter={setRouteFilter}
            driverFilter={driverFilter} setDriverFilter={setDriverFilter}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            delayOnly={delayOnly} setDelayOnly={setDelayOnly}
            incidentsOnly={incidentsOnly} setIncidentsOnly={setIncidentsOnly}
            lineOptions={lineOptions} driverOptions={driverOptions}
          />
        )}
      </div>
    </div>
  );
}

/* ── KPI ─────────────────────────────────────────────────────────────────── */
function Kpi({ label, value, secondary, accent, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testId}
      className={`border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 text-left min-w-[110px] ${onClick ? "hover:bg-zinc-700 cursor-pointer" : ""}`}>
      <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-zinc-400">{label}</div>
      <div className="font-display font-black text-xl leading-none tracking-tighter mt-0.5" style={accent ? { color: accent } : undefined}>
        {value}
        {secondary && <span className="text-xs text-zinc-400 font-mono ml-1">{secondary}</span>}
      </div>
    </Tag>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${active ? "border-zinc-900 bg-zinc-900 text-[#FFC60A]" : "border-zinc-300 hover:border-zinc-900 text-zinc-700"}`}>
      {label}
    </button>
  );
}

/* ── Collapsed side strip (left or right) ───────────────────────────────── */
function CollapsedStrip({ side, count, label, tone = "neutral", onOpen, testId }) {
  const isLeft = side === "left";
  const borderClass = isLeft ? "border-r" : "border-l";
  const Icon = isLeft ? ChevronRight : ChevronLeft;
  const toneColor = tone === "danger" && count > 0 ? "bg-red-600 text-white" : "bg-zinc-900 text-[#FFC60A]";
  return (
    <button onClick={onOpen} data-testid={testId}
      title={`Ouvrir le panneau ${label}`}
      className={`w-8 ${borderClass} border-zinc-900 bg-white hover:bg-[#FFC60A] flex flex-col items-center justify-between py-2 flex-shrink-0 transition-colors group`}>
      <Icon size={14} className="text-zinc-700 group-hover:text-zinc-900" />
      <div className="flex-1 flex items-center justify-center">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-700"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          {label}
        </div>
      </div>
      {count > 0 && (
        <span className={`text-[9px] font-mono font-bold px-1 py-0.5 ${toneColor} border border-zinc-900 min-w-[20px] text-center`}>
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}



/* ── Replay bar ─────────────────────────────────────────────────────────── */
function ReplayBar({
  date, setDate, mode, setMode, payload, loading,
  playing, setPlaying, speed, setSpeed, cursor, setCursor,
  incidents, events, onLoad, selKind, selLine, selVeh,
}) {
  const start = payload ? new Date(payload.range.from_iso).getTime() : null;
  const end = payload ? new Date(payload.range.to_iso).getTime() : null;
  const span = start && end ? Math.max(end - start, 60_000) : 0;
  const cursorTs = start ? new Date(start + cursor) : null;
  const ready = !!payload && (payload.total_points || 0) > 0;

  // Tick marks positions
  const ticks = useMemo(() => {
    if (!ready) return [];
    return [
      ...(incidents || []).map((i) => ({
        kind: "incident",
        pos: Math.max(0, Math.min(1, (new Date(i.created_at).getTime() - start) / span)),
        label: `${(i.type || "Incident")} · ${(i.created_at || "").slice(11, 16)}`,
      })),
      ...(events || []).map((e) => ({
        kind: (e.delay_minutes || 0) >= 3 ? "delay" : "stop",
        pos: Math.max(0, Math.min(1, (new Date(e.created_at).getTime() - start) / span)),
        label: `${e.stop_name} · ${(e.delay_minutes || 0) > 0 ? "+" + e.delay_minutes + "m" : "à l'heure"}`,
      })),
    ];
  }, [ready, incidents, events, start, span]);

  const canPlay = ready && cursor < span;

  return (
    <div className="bg-zinc-100 border-b border-zinc-300 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="sl-replay-bar">
      <div className="flex items-center gap-1.5">
        <Calendar size={12} className="text-zinc-600" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sl-replay-date"
          className="text-xs font-mono border border-zinc-300 px-2 py-1 bg-white" />
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => setMode("vehicle")} data-testid="sl-replay-mode-veh"
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${mode === "vehicle" ? "bg-zinc-900 text-[#FFC60A] border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
          Véhicule
        </button>
        <button onClick={() => setMode("line")} data-testid="sl-replay-mode-line"
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${mode === "line" ? "bg-zinc-900 text-[#FFC60A] border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
          Ligne
        </button>
      </div>
      <div className="text-[10px] font-mono text-zinc-600">
        {mode === "vehicle" ? (selVeh ? `Véhicule ${selVeh}` : "Sélectionnez un véhicule") :
          (selLine ? `Ligne ${selLine}` : "Sélectionnez une ligne")}
      </div>
      <button onClick={onLoad} disabled={loading} data-testid="sl-replay-load"
        className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
        {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Charger
      </button>

      {ready && (
        <>
          <div className="h-5 w-px bg-zinc-300" />
          <button onClick={() => setCursor(0)} data-testid="sl-replay-rewind"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1">
            <Rewind size={10} /> Début
          </button>
          <button onClick={() => setPlaying((p) => !p)} disabled={!canPlay} data-testid="sl-replay-play"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
            {playing ? <><Pause size={10} /> Pause</> : <><Play size={10} /> Lecture</>}
          </button>
          <div className="flex items-center gap-1">
            {REPLAY_SPEEDS.map((s) => (
              <button key={s} onClick={() => setSpeed(s)} data-testid={`sl-replay-speed-${s}`}
                className={`text-[10px] font-mono px-1.5 py-1 border ${speed === s ? "bg-[#FFC60A] border-zinc-900 font-bold" : "border-zinc-300 hover:border-zinc-900"}`}>
                {s}×
              </button>
            ))}
          </div>
          <button onClick={() => setCursor(span)} data-testid="sl-replay-ff"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1">
            <FastForward size={10} /> Fin
          </button>
          <div className="flex-1 min-w-[200px] relative">
            <input type="range" min={0} max={span} step={1000} value={cursor}
              onChange={(e) => setCursor(parseInt(e.target.value, 10))}
              data-testid="sl-replay-cursor"
              className="w-full accent-[#FFC60A]" />
            {/* Tick marks layer */}
            <div className="absolute top-0 left-0 right-0 h-full pointer-events-none">
              {ticks.map((t, i) => (
                <span key={i} title={t.label}
                  className={`absolute top-1.5 w-1 h-2 ${t.kind === "incident" ? "bg-red-600" : t.kind === "delay" ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ left: `${t.pos * 100}%` }} />
              ))}
            </div>
          </div>
          <div className="text-[10px] font-mono tabular-nums text-zinc-700 min-w-[130px] text-right">
            {cursorTs ? cursorTs.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"} ·
            <span className="text-zinc-500"> {(new Date(start)).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })} → {(new Date(end)).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        </>
      )}
      {!ready && !loading && payload && (payload.total_points || 0) === 0 && (
        <span className="text-[10px] font-mono text-amber-700 italic inline-flex items-center gap-1" data-testid="sl-replay-empty">
          <AlertCircle size={11} /> Aucune trace GPS pour cette date · changez le jour ou la cible
        </span>
      )}
      {!ready && !loading && !payload && (
        <span className="text-[10px] font-mono text-zinc-500 italic">Chargez une journée pour démarrer le replay.</span>
      )}
    </div>
  );
}

/* ── Lines panel (left) ─────────────────────────────────────────────────── */
function LinesPanel({ lines, activeRoute, onSelect, vehicles }) {
  const enriched = useMemo(() => {
    if (lines.length > 0) return lines;
    const m = new Map();
    vehicles.forEach((v) => {
      if (!v.route_short_name) return;
      if (!m.has(v.route_short_name)) m.set(v.route_short_name, { route_short_name: v.route_short_name, buses_count: 0, delays_count: 0, drivers: new Set() });
      const x = m.get(v.route_short_name);
      x.buses_count += 1;
      if ((v.forced_delay_minutes || 0) >= 3) x.delays_count += 1;
      if (v.driver_name) x.drivers.add(v.driver_name);
    });
    return Array.from(m.values()).map((x) => ({ ...x, drivers_count: x.drivers.size, incidents_open: 0, avg_delay_min: 0, punctuality_pct: 100 }));
  }, [lines, vehicles]);

  return (
    <aside className="w-[260px] border-r border-zinc-900 bg-white flex flex-col overflow-hidden flex-shrink-0" data-testid="sl-lines-panel">
      <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-50 flex-shrink-0">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1"><RouteIcon size={11} /> Lignes actives</div>
        <div className="font-display font-black text-sm tracking-tighter">{enriched.length} ligne{enriched.length > 1 ? "s" : ""}</div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {enriched.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-400">Aucune ligne active actuellement.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {enriched.map((l) => {
              const color = lineColor(l.route_short_name);
              const isActive = activeRoute === l.route_short_name;
              return (
                <li key={l.route_short_name}>
                  <button onClick={() => onSelect(l.route_short_name)}
                    data-testid={`sl-line-${l.route_short_name}`}
                    className={`w-full px-3 py-2 text-left hover:bg-zinc-50 flex items-start gap-2 border-l-4 ${isActive ? "bg-[#FFF7D6]" : ""}`}
                    style={{ borderLeftColor: isActive ? color : "transparent" }}>
                    <span className="font-display font-black text-base tracking-tighter px-1.5 py-0.5 border border-zinc-900 text-white flex-shrink-0" style={{ background: color }}>
                      {l.route_short_name}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] font-mono text-zinc-600 mt-0.5">
                        <span><strong className="text-zinc-900">{l.buses_count}</strong> bus</span>
                        <span><strong className="text-zinc-900">{l.drivers_count}</strong> chauf.</span>
                        <span className={l.delays_count > 0 ? "text-amber-700 font-bold" : ""}>
                          {l.delays_count > 0 ? `▲ ${l.delays_count} retard${l.delays_count > 1 ? "s" : ""}` : "à l'heure"}
                        </span>
                        <span className={l.incidents_open > 0 ? "text-red-700 font-bold" : ""}>
                          {l.incidents_open > 0 ? `! ${l.incidents_open} inc.` : "0 inc."}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-[10px] font-mono">
                        <span className={`font-bold ${l.punctuality_pct >= 90 ? "text-emerald-700" : l.punctuality_pct >= 70 ? "text-amber-700" : "text-red-700"}`}>
                          {l.punctuality_pct}% ponct.
                        </span>
                        {l.avg_delay_min > 0 && <span className="text-zinc-500">· {l.avg_delay_min}m</span>}
                      </div>
                    </div>
                    <ChevronRight size={12} className="text-zinc-400 mt-1 flex-shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

/* ── Context panel router ───────────────────────────────────────────────── */
function ContextPanel({ sel, vehicles, incidents, onClose, onSelect, onFlyTo, onRefresh }) {
  if (sel.kind === "bus") {
    const v = vehicles.find((x) => x.vehicle_id === sel.vehicle_id);
    if (!v) return <EmptyPanel msg="Bus introuvable." onClose={onClose} />;
    return <BusPanel v={v} onClose={onClose} onLineClick={(rn) => onSelect({ kind: "line", route_short_name: rn })} />;
  }
  if (sel.kind === "line") return <LinePanel route_short_name={sel.route_short_name} onClose={onClose} onSelectBus={(v) => { onSelect({ kind: "bus", vehicle_id: v.vehicle_id }); onFlyTo({ lat: v.lat, lon: v.lon, zoom: 14 }); }} />;
  if (sel.kind === "stop") return <StopPanel stop_id={sel.stop_id} onClose={onClose} />;
  return <AlertsPanel incidents={incidents} vehicles={vehicles}
    onSelectBus={(v) => { onSelect({ kind: "bus", vehicle_id: v.vehicle_id }); onFlyTo({ lat: v.lat, lon: v.lon, zoom: 14 }); }}
    onSelectLine={(rn) => onSelect({ kind: "line", route_short_name: rn })}
    onRefresh={onRefresh} />;
}

function EmptyPanel({ msg, onClose }) {
  return (
    <div className="p-6 text-center text-sm text-zinc-500">
      <p>{msg}</p>
      <button onClick={onClose} className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-700 underline">Retour</button>
    </div>
  );
}

/* ── Alerts panel ───────────────────────────────────────────────────────── */
const INC_TYPE_FR = {
  delay: "Retard", route_deviation: "Hors itinéraire", gps_absent: "GPS absent",
  trip_mismatch: "Hors tournée", driver_conflict: "Conflit chauffeur",
  speeding: "Excès de vitesse", vehicle_offline: "Bus hors ligne",
};

function AlertsPanel({ incidents, vehicles, onSelectBus, onSelectLine, onRefresh }) {
  const [busy, setBusy] = useState({});
  const [hidden, setHidden] = useState(() => new Set());
  const act = async (incId, kind) => {
    setBusy((s) => ({ ...s, [incId + kind]: true }));
    try {
      if (kind === "resolve") {
        await api.post(`/incidents/${incId}/resolve`, { action_taken: "Résolu depuis Suivi Live" });
        toast.success("Incident résolu");
        setHidden((h) => new Set(h).add(incId));
      } else if (kind === "ignore") {
        await api.post(`/incidents/${incId}/ignore`, { comment: "Ignoré depuis Suivi Live" });
        toast.success("Incident ignoré");
        setHidden((h) => new Set(h).add(incId));
      } else if (kind === "assign") {
        await api.post(`/incidents/${incId}/assign`, { user_id: window.__LOGIBUS_ME__?.id || "self", note: "Assigné depuis Suivi Live" });
        toast.success("Incident assigné");
      }
      setTimeout(() => onRefresh?.(), 200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy((s) => { const c = { ...s }; delete c[incId + kind]; return c; }); }
  };
  const onView = (inc) => {
    const incVehId = inc.vehicle_id || (inc.extra || {}).vehicle_id;
    const v = vehicles.find((x) => x.vehicle_id === incVehId || x.license_plate === inc.vehicle_label || x.label === inc.vehicle_label);
    if (v) { onSelectBus(v); return; }
    if (inc.route_short_name) { onSelectLine(inc.route_short_name); toast.info(`Bus ${inc.vehicle_label || ""} hors ligne — ligne ${inc.route_short_name}.`); return; }
    toast.warning("Aucune position récente pour ce véhicule.");
  };
  const visible = incidents.filter((i) => !hidden.has(i.id))
    .sort((a, b) => ({ critical: 4, high: 3, medium: 2, warning: 2, low: 1 }[(b.severity || "").toLowerCase()] || 0)
                  - ({ critical: 4, high: 3, medium: 2, warning: 2, low: 1 }[(a.severity || "").toLowerCase()] || 0));
  return (
    <>
      <div className="px-3 py-2 border-b border-zinc-900 bg-red-50 flex items-center gap-2 flex-shrink-0">
        <ShieldAlert size={14} className="text-red-700" />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-red-800">Alertes opérationnelles</div>
          <div className="font-display font-black text-sm tracking-tight">{visible.length} en cours</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="sl-alerts-list">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-xs text-emerald-700">
            <Check size={24} className="mx-auto text-emerald-400 mb-1" /> Aucune alerte en cours.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {visible.slice(0, 30).map((i) => {
              const sev = (i.severity || "").toLowerCase();
              const sevColor = sev === "critical" ? "#DC2626" : sev === "high" ? "#EA580C" : sev === "medium" || sev === "warning" ? "#F59E0B" : "#71717A";
              const typeLabel = INC_TYPE_FR[(i.type || "").toLowerCase()] || (i.type || "Incident");
              const v = vehicles.find((x) => x.vehicle_id === (i.vehicle_id || (i.extra || {}).vehicle_id));
              const plate = v?.label || v?.license_plate || i.vehicle_label || i.vehicle_id;
              return (
                <li key={i.id} className="px-3 py-2 hover:bg-zinc-50" data-testid={`sl-alert-${i.id}`}>
                  <div className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: sevColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold uppercase">{plate ? `Bus ${plate} · ${typeLabel}` : typeLabel}</div>
                      <div className="text-[10px] font-mono text-zinc-600 line-clamp-2 mt-0.5">{i.message || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 pl-3.5 flex-wrap">
                    <button onClick={() => onView(i)} data-testid={`sl-alert-view-${i.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 inline-flex items-center gap-1">
                      <ArrowRight size={9} /> Voir
                    </button>
                    <button onClick={() => act(i.id, "assign")} disabled={busy[i.id + "assign"]}
                      data-testid={`sl-alert-assign-${i.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 inline-flex items-center gap-1 disabled:opacity-50">
                      {busy[i.id + "assign"] ? <Loader2 size={9} className="animate-spin" /> : <UserCheck size={9} />} Assigner
                    </button>
                    <button onClick={() => act(i.id, "resolve")} disabled={busy[i.id + "resolve"]}
                      data-testid={`sl-alert-resolve-${i.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50">
                      {busy[i.id + "resolve"] ? <Loader2 size={9} className="animate-spin" /> : <Check size={9} />} Résoudre
                    </button>
                    <button onClick={() => act(i.id, "ignore")} disabled={busy[i.id + "ignore"]}
                      data-testid={`sl-alert-ignore-${i.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 inline-flex items-center gap-1 disabled:opacity-50">
                      {busy[i.id + "ignore"] ? <Loader2 size={9} className="animate-spin" /> : <EyeOff size={9} />} Ignorer
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ── Bus panel ──────────────────────────────────────────────────────────── */
function BusPanel({ v, onClose, onLineClick }) {
  const [sending, setSending] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const phone = v.driver_phone || v.phone;
  const delay = Number(v.forced_delay_minutes || 0);
  const meta = BUS_STATE[(v.status || "").toUpperCase()] || BUS_STATE.OFFLINE;

  const send = async () => {
    if (!msg.trim() || !v.driver_id) return;
    setSending(true);
    try {
      await api.post("/control-room/send-message", {
        driver_id: v.driver_id, body: msg.trim(),
        related_vehicle_id: v.vehicle_id, channel: "internal",
      });
      toast.success("Message envoyé au chauffeur");
      setMsg(""); setMsgOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-900 text-white flex items-start gap-2 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400 inline-flex items-center gap-1"><Bus size={10} /> Véhicule</div>
          <div className="font-display font-black text-base tracking-tighter truncate">{v.label || v.license_plate || v.vehicle_id}</div>
          <div className="mt-0.5 inline-flex items-center gap-1.5 text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} /> {meta.label}
            {delay > 0 && <span className="text-amber-400">· +{delay} min</span>}
          </div>
        </div>
        <button onClick={onClose} data-testid="sl-bus-close" className="text-zinc-400 hover:text-white"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Operational priority block */}
        <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
          <Row label="Ligne" value={v.route_short_name ? (
            <button onClick={() => onLineClick(v.route_short_name)} className="font-bold hover:underline" style={{ color: lineColor(v.route_short_name) }}>
              {v.route_short_name}
            </button>
          ) : "—"} />
          <Row label="Chauffeur" value={v.driver_name || "—"} />
          <Row label="Prochain arrêt" value={v.next_stop_name || "—"} />
          <Row label="ETA" value={v.eta_minutes != null ? `${v.eta_minutes} min` : "—"} />
          <Row label="Retard" value={delay > 0 ? `+${delay} min` : "à l'heure"} tone={delay > 0 ? "amber" : "ok"} />
          <Row label="Statut" value={meta.label} />
        </div>
        {/* Timeline of the active trip */}
        <VehicleTimeline vehicle_id={v.vehicle_id} />
        {/* Secondary tech */}
        <Row label="Vitesse" value={v.speed != null ? `${Math.round(v.speed)} km/h` : "—"} />
        <Row label="Source GPS" value={v.gps_source || v.source || "—"} mono />
        <Row label="Bluetooth" value={v.has_bluetooth_tag ? v.bluetooth_tag_label || "OK" : "—"} />
        {v.battery_level != null && <Row label="Batterie tablette" value={`${v.battery_level}%`} />}
        {v.score != null && <Row label="Score conduite" value={`${v.score}/100`} />}
        {v.warnings?.length > 0 && (
          <div className="px-3 py-2 border-t border-zinc-100 bg-amber-50">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800 mb-1">Avertissements</div>
            <ul className="text-[11px] text-amber-900 list-disc list-inside space-y-0.5">
              {v.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>
      <div className="border-t border-zinc-900 p-2 flex flex-col gap-1.5 flex-shrink-0 bg-zinc-50">
        {phone && (
          <a href={`tel:${phone}`} data-testid="sl-bus-call"
            className="text-[11px] font-bold uppercase tracking-widest px-3 py-2 border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center justify-center gap-2">
            <Phone size={12} /> Appeler · {phone}
          </a>
        )}
        {v.driver_id && (msgOpen ? (
          <div className="border border-zinc-900 bg-white p-2 flex flex-col gap-1.5" data-testid="sl-bus-msg-form">
            <textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={320}
              placeholder="Message ≤ 320 car." data-testid="sl-bus-msg-input"
              className="border border-zinc-300 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-zinc-900" />
            <div className="flex items-center gap-1 justify-end">
              <button onClick={() => { setMsgOpen(false); setMsg(""); }} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:bg-zinc-100">Annuler</button>
              <button onClick={send} disabled={sending || !msg.trim()} data-testid="sl-bus-msg-send"
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
                {sending ? <Loader2 size={9} className="animate-spin" /> : <ArrowRight size={9} />} Envoyer
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setMsgOpen(true)} data-testid="sl-bus-msg-open"
            className="text-[11px] font-bold uppercase tracking-widest px-3 py-2 border border-zinc-900 bg-white hover:bg-[#FFC60A] inline-flex items-center justify-center gap-2">
            <MessageSquare size={12} /> Envoyer un message
          </button>
        ))}
      </div>
    </>
  );
}

function Row({ label, value, mono = false, tone }) {
  const toneClass = tone === "amber" ? "text-amber-700 font-bold" : tone === "ok" ? "text-emerald-700" : "";
  return (
    <div className="flex items-center gap-3 text-xs py-0.5">
      <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 w-28 flex-shrink-0">{label}</span>
      <span className={`flex-1 min-w-0 truncate ${mono ? "font-mono" : ""} ${toneClass}`}>{value}</span>
    </div>
  );
}

/* ── Vehicle Timeline ───────────────────────────────────────────────────── */
function VehicleTimeline({ vehicle_id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/logibus/live/vehicle-timeline?vehicle_id=${encodeURIComponent(vehicle_id)}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ stops: [], found: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [vehicle_id]);
  if (loading) {
    return (
      <div className="px-3 py-3 border-b border-zinc-100" data-testid="sl-veh-timeline-loading">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-1">Timeline</div>
        <div className="text-xs text-zinc-400 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Calcul…</div>
      </div>
    );
  }
  const stops = data?.stops || [];
  if (stops.length === 0) {
    return (
      <div className="px-3 py-3 border-b border-zinc-100" data-testid="sl-veh-timeline-empty">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-1">Timeline</div>
        <div className="text-xs text-zinc-400">Aucune tournée détectée pour ce véhicule.</div>
      </div>
    );
  }
  return (
    <div className="border-b border-zinc-100" data-testid="sl-veh-timeline">
      <div className="px-3 py-2 bg-zinc-50 flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-700">Timeline · {stops.length} arrêts</div>
        {data.headsign && <div className="text-[10px] font-mono text-zinc-500 truncate max-w-[180px]">→ {data.headsign}</div>}
      </div>
      <ol className="px-3 py-2 space-y-1">
        {stops.map((s, i) => {
          const isPassed = s.status === "passed";
          const isCurrent = s.status === "current";
          const dotColor = isPassed ? "#10B981" : isCurrent ? "#F59E0B" : "#A1A1AA";
          const Icon = isPassed ? CheckCircle2 : isCurrent ? AlertCircle : Circle;
          const delay = Number(s.delay_minutes || 0);
          return (
            <li key={s.stop_id + "-" + i} className="flex items-start gap-2 text-xs" data-testid={`sl-timeline-stop-${s.stop_sequence}`}>
              <div className="flex flex-col items-center pt-0.5 flex-shrink-0">
                <Icon size={14} style={{ color: dotColor }} />
                {i < stops.length - 1 && <span className="w-0.5 h-5 mt-0.5" style={{ background: isPassed ? "#10B981" : "#E4E4E7" }} />}
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <div className={`truncate ${isCurrent ? "font-bold" : isPassed ? "text-zinc-500" : ""}`}>{s.stop_name}</div>
                <div className="flex items-center gap-2 text-[10px] font-mono">
                  <span className="text-zinc-500">{s.scheduled || "—"}</span>
                  {s.real && <span className="text-zinc-700">→ {s.real}</span>}
                  {delay > 0 && <span className={delay >= 7 ? "text-red-700 font-bold" : delay >= 3 ? "text-amber-700 font-bold" : "text-zinc-500"}>+{delay}m</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ── Line panel ─────────────────────────────────────────────────────────── */
function LinePanel({ route_short_name, onClose, onSelectBus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/control-room/line-summary/${encodeURIComponent(route_short_name)}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [route_short_name]);
  const color = lineColor(route_short_name);
  return (
    <>
      <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-900 text-white flex items-start gap-2 flex-shrink-0">
        <span className="font-display font-black text-2xl tracking-tighter px-2 py-0.5 border border-zinc-100" style={{ background: color }}>{route_short_name}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Ligne</div>
          <div className="font-display font-bold text-sm">Détail temps réel</div>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="sl-line-panel">
        {loading ? (
          <div className="p-6 text-center text-xs font-mono text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Calcul…</div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 border-b border-zinc-100">
              <Stat label="Bus" value={data.vehicles_count} />
              <Stat label="Chauffeurs" value={data.drivers_count} />
              <Stat label="Retard moyen" value={`${data.avg_delay_min} min`} />
              <Stat label="Ponctualité" value={`${data.punctuality_pct}%`} accent={data.punctuality_pct >= 90 ? "#10B981" : "#F59E0B"} />
            </div>
            {data.incidents_open > 0 && (
              <div className="px-3 py-2 border-b border-zinc-100 bg-red-50 text-xs">
                <strong className="text-red-800">{data.incidents_open}</strong> incident{data.incidents_open > 1 ? "s" : ""} ouvert{data.incidents_open > 1 ? "s" : ""}
              </div>
            )}
            <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Véhicules</div>
            {data.vehicles.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-400 text-center">Aucun véhicule actif.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {data.vehicles.map((v) => {
                  const meta = BUS_STATE[(v.status || "").toUpperCase()] || BUS_STATE.OFFLINE;
                  return (
                    <li key={v.vehicle_id}>
                      <button onClick={() => onSelectBus(v)} data-testid={`sl-line-veh-${v.vehicle_id}`}
                        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-zinc-50">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold truncate">{v.label || v.license_plate || v.vehicle_id}</div>
                          <div className="text-[10px] font-mono text-zinc-500 truncate">{v.driver_name || "—"} · {meta.label}</div>
                        </div>
                        {v.forced_delay_minutes > 0 && <span className="text-amber-700 text-xs font-mono font-bold">+{v.forced_delay_minutes}m</span>}
                        <ArrowRight size={10} className="text-zinc-400" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : <EmptyPanel msg="Ligne introuvable" onClose={onClose} />}
      </div>
    </>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="px-3 py-2 border-r border-zinc-100 last:border-r-0">
      <div className="text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</div>
      <div className="font-display font-black text-lg leading-none tracking-tighter mt-0.5" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

/* ── Stop panel ─────────────────────────────────────────────────────────── */
function StopPanel({ stop_id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/control-room/stop-board/${encodeURIComponent(stop_id)}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [stop_id]);
  return (
    <>
      <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-900 text-white flex items-start gap-2 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400 inline-flex items-center gap-1"><MapPin size={10} /> Arrêt</div>
          <div className="font-display font-black text-base tracking-tighter truncate">{data?.stop_name || stop_id}</div>
          {data?.city && <div className="text-[10px] text-zinc-400">{data.city}</div>}
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="sl-stop-panel">
        {loading ? (
          <div className="p-6 text-center text-xs font-mono text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Calcul…</div>
        ) : (data?.rows?.length || 0) === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-400">Aucun passage prévu.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <th className="text-left px-2 py-1.5">Ligne</th>
                <th className="text-left px-2 py-1.5">Théorique</th>
                <th className="text-left px-2 py-1.5">Réel</th>
                <th className="text-left px-2 py-1.5">Retard</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.rows.map((r, i) => (
                <tr key={i} className="hover:bg-zinc-50">
                  <td className="px-2 py-1.5 font-bold" style={{ color: lineColor(r.route_short_name) }}>{r.route_short_name || "—"}</td>
                  <td className="px-2 py-1.5 font-mono">{r.scheduled?.slice(0, 5)}</td>
                  <td className="px-2 py-1.5 font-mono">{r.real || "—"}</td>
                  <td className={`px-2 py-1.5 font-mono font-bold ${r.delay_min >= 7 ? "text-red-700" : r.delay_min >= 3 ? "text-amber-700" : "text-zinc-600"}`}>
                    {r.delay_min > 0 ? `+${r.delay_min}m` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ── Exploitation table ─────────────────────────────────────────────────── */
function ExploitationTable({
  vehicles, sel, onSelect, onSelectAndShowMap, incidentsByVehicle,
  routeFilter, setRouteFilter, driverFilter, setDriverFilter,
  statusFilter, setStatusFilter, delayOnly, setDelayOnly,
  incidentsOnly, setIncidentsOnly, lineOptions, driverOptions,
}) {
  const [sortKey, setSortKey] = useState("delay_desc");
  const sorted = useMemo(() => {
    const list = [...vehicles];
    const key = sortKey;
    const dir = key.endsWith("_desc") ? -1 : 1;
    const base = key.replace(/_(asc|desc)$/, "");
    const get = (v) => {
      switch (base) {
        case "line": return v.route_short_name || "";
        case "label": return v.label || v.license_plate || v.vehicle_id || "";
        case "driver": return v.driver_name || "";
        case "status": return v.status || "";
        case "delay": return Number(v.forced_delay_minutes || 0);
        case "eta": return Number(v.eta_minutes ?? 9999);
        case "incidents": return incidentsByVehicle[v.vehicle_id] || 0;
        case "trip": return v.headsign || v.trip_status || "";
        default: return 0;
      }
    };
    list.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [vehicles, sortKey, incidentsByVehicle]);

  const SortHeader = ({ k, label, align = "left" }) => {
    const active = sortKey.startsWith(k);
    const dir = active && sortKey.endsWith("_asc") ? "asc" : "desc";
    return (
      <th className={`text-${align} px-3 py-2 select-none cursor-pointer hover:bg-zinc-100`}
        onClick={() => setSortKey(active && dir === "desc" ? `${k}_asc` : `${k}_desc`)}
        data-testid={`sl-sort-${k}`}>
        {label}{active ? (dir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  };
  return (
    <section className="flex-1 overflow-auto bg-white" data-testid="sl-exploit-table">
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-900 px-3 py-2 flex items-center gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Vue exploitation · {sorted.length} bus</div>
        <div className="flex-1" />
        <Filter size={12} className="text-zinc-500" />
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} data-testid="sl-exp-filter-line"
          className="text-xs font-mono p-1 outline-none bg-white border border-zinc-300 hover:border-zinc-900">
          <option value="">Toutes lignes</option>
          {lineOptions.map((l) => <option key={l} value={l}>{`Ligne ${l}`}</option>)}
        </select>
        <select value={driverFilter} onChange={(e) => setDriverFilter(e.target.value)} data-testid="sl-exp-filter-driver"
          className="text-xs font-mono p-1 outline-none bg-white border border-zinc-300 hover:border-zinc-900 max-w-[180px]">
          <option value="">Tous chauffeurs</option>
          {driverOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="sl-exp-filter-status"
          className="text-xs font-mono p-1 outline-none bg-white border border-zinc-300 hover:border-zinc-900">
          <option value="">Tous statuts</option>
          {Object.entries(BUS_STATE).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <Chip label="Retards" active={delayOnly} onClick={() => setDelayOnly(!delayOnly)} testId="sl-exp-filter-delay" />
        <Chip label="Incidents" active={incidentsOnly} onClick={() => setIncidentsOnly(!incidentsOnly)} testId="sl-exp-filter-inc" />
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <SortHeader k="line" label="Ligne" />
            <SortHeader k="label" label="Véhicule" />
            <SortHeader k="driver" label="Chauffeur" />
            <SortHeader k="status" label="Statut" />
            <SortHeader k="delay" label="Retard" />
            <th className="text-left px-3 py-2">Prochain arrêt</th>
            <SortHeader k="eta" label="ETA" />
            <SortHeader k="incidents" label="Inc." />
            <SortHeader k="trip" label="Tournée" />
            <th className="text-right px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {sorted.length === 0 && (
            <tr><td colSpan={10} className="px-3 py-8 text-center text-xs text-zinc-400">Aucun véhicule ne correspond aux filtres.</td></tr>
          )}
          {sorted.map((v) => {
            const meta = BUS_STATE[(v.status || "").toUpperCase()] || BUS_STATE.OFFLINE;
            const delay = Number(v.forced_delay_minutes || 0);
            const incCount = incidentsByVehicle[v.vehicle_id] || 0;
            const isSel = sel?.kind === "bus" && sel?.vehicle_id === v.vehicle_id;
            const trip = v.headsign || (v.trip_status === "active" ? "En tournée" : v.trip_status === "expired" ? "Terminée" : v.trip_status === "future" ? "À venir" : v.trip_status === "no_trip_detected" ? "—" : (v.trip_status || "—"));
            return (
              <tr key={v.vehicle_id}
                onClick={() => onSelect(v)}
                onDoubleClick={() => onSelectAndShowMap(v)}
                data-testid={`sl-row-${v.vehicle_id}`}
                className={`cursor-pointer ${isSel ? "bg-[#FFF7D6] border-l-4 border-l-[#FFC60A]" : "hover:bg-zinc-50"}`}>
                <td className="px-3 py-2">
                  {v.route_short_name ? (
                    <span className="font-display font-black text-base tracking-tighter px-1.5 py-0.5 text-white" style={{ background: lineColor(v.route_short_name) }}>{v.route_short_name}</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{v.label || v.license_plate || v.vehicle_id}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[160px]">{v.driver_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                    {meta.label}
                  </span>
                </td>
                <td className={`px-3 py-2 font-mono text-xs font-bold ${delay >= 7 ? "text-red-700" : delay >= 3 ? "text-amber-700" : "text-zinc-500"}`}>
                  {delay > 0 ? `+${delay} min` : "—"}
                </td>
                <td className="px-3 py-2 text-xs truncate max-w-[180px]">{v.next_stop_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{v.eta_minutes != null ? `${v.eta_minutes}m` : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {incCount > 0 ? <span className="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 bg-red-600 text-white font-mono font-bold rounded">{incCount}</span> : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono truncate max-w-[150px]">{trip}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={(e) => { e.stopPropagation(); onSelectAndShowMap(v); }}
                    data-testid={`sl-row-zoom-${v.vehicle_id}`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-[#FFC60A] inline-flex items-center gap-1">
                    <MapIcon size={9} /> Carte
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}


/* ── ANALYTIQUE Tab ─────────────────────────────────────────────────────── */
function AnalyticsTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/logibus/analytics/overview?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || { events: 0, delays: 0, critical: 0, incidents: 0, avg_delay_min: 0 };

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-zinc-100" data-testid="saeiv-analytics-tab">
      {/* Header */}
      <header className="bg-zinc-900 text-white border-b-2 border-[#FFC60A] flex items-center px-3 py-2 gap-3 flex-wrap flex-shrink-0">
        <div className="flex items-center gap-2 mr-2">
          <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">SAEIV · Supervision analytique</div>
          <div className="font-display font-black text-lg tracking-tighter">ANALYTIQUE</div>
        </div>
        <Kpi label="Événements"      value={totals.events}        testId="an-kpi-events" />
        <Kpi label="Retards ≥ 3 min"  value={totals.delays}        accent={totals.delays ? "#F59E0B" : null} testId="an-kpi-delays" />
        <Kpi label="Retards critiques" value={totals.critical}    accent={totals.critical ? "#DC2626" : null} testId="an-kpi-critical" />
        <Kpi label="Incidents"       value={totals.incidents}     accent={totals.incidents ? "#EF4444" : null} testId="an-kpi-incidents" />
        <Kpi label="Retard moyen"    value={`${totals.avg_delay_min}m`} testId="an-kpi-avg" />
        <div className="flex-1" />
        <div className="flex items-center gap-1" data-testid="an-period">
          {[7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`an-period-${d}`}
              className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest ${days === d ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "border-zinc-700 hover:bg-zinc-800"}`}>
              {d} jours
            </button>
          ))}
        </div>
        <button onClick={load} disabled={loading} data-testid="an-refresh"
          className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:bg-zinc-800 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && !data ? (
          <div className="text-center py-12 text-zinc-500 text-sm"><Loader2 size={20} className="inline animate-spin mr-2" /> Chargement des analyses…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Daily trend */}
            <Card title="Tendance quotidienne" icon={Activity} span="lg:col-span-2 xl:col-span-3" testId="an-card-daily">
              {(data?.daily?.length || 0) === 0 ? (
                <Empty>Aucune donnée sur cette période.</Empty>
              ) : (
                <DailyBars rows={data.daily} />
              )}
            </Card>

            {/* Punctuality by line */}
            <Card title="Ponctualité par ligne" icon={BarChart3} testId="an-card-punctuality">
              {(data?.punctuality_by_line?.length || 0) === 0 ? <Empty>Aucune ligne suivie.</Empty> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="text-left py-1.5">Ligne</th>
                    <th className="text-right py-1.5">Événements</th>
                    <th className="text-right py-1.5">Retards</th>
                    <th className="text-right py-1.5">Retard moy.</th>
                    <th className="text-right py-1.5">Ponctualité</th>
                  </tr></thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.punctuality_by_line.map((r) => (
                      <tr key={r.route_short_name} data-testid={`an-line-${r.route_short_name}`}>
                        <td className="py-1.5"><LineBadge rn={r.route_short_name} /></td>
                        <td className="py-1.5 text-right font-mono">{r.events}</td>
                        <td className="py-1.5 text-right font-mono text-amber-700 font-bold">{r.delays}</td>
                        <td className="py-1.5 text-right font-mono">{r.avg_delay_min}m</td>
                        <td className={`py-1.5 text-right font-mono font-bold ${r.punctuality_pct >= 90 ? "text-emerald-700" : r.punctuality_pct >= 70 ? "text-amber-700" : "text-red-700"}`}>{r.punctuality_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            {/* Incidents by line */}
            <Card title="Incidents par ligne" icon={ShieldAlert} testId="an-card-inc-line">
              {(data?.incidents_by_line?.length || 0) === 0 ? <Empty>Aucun incident.</Empty> : (
                <ul className="divide-y divide-zinc-100">
                  {data.incidents_by_line.map((r) => (
                    <li key={r.route_short_name} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`an-inc-line-${r.route_short_name}`}>
                      <LineBadge rn={r.route_short_name} />
                      <div className="flex-1" />
                      <span className="font-mono font-bold text-red-700">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Hotspots */}
            <Card title="Hotspots géographiques" icon={Flame} testId="an-card-hotspots">
              {(data?.hotspots?.length || 0) === 0 ? <Empty>Aucun hotspot détecté.</Empty> : (
                <ul className="divide-y divide-zinc-100">
                  {data.hotspots.map((h, i) => (
                    <li key={`${h.stop_name}-${i}`} className="py-1.5 text-xs" data-testid={`an-hotspot-${i}`}>
                      <div className="flex items-center gap-2">
                        <MapPin size={12} className="text-orange-600 flex-shrink-0" />
                        <span className="font-bold truncate flex-1">{h.stop_name}</span>
                        <span className="font-mono text-zinc-500">{h.count}×</span>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 pl-5">retard moyen +{h.avg_delay_min}m</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Incidents by vehicle */}
            <Card title="Top véhicules à risque" icon={Bus} testId="an-card-inc-veh">
              {(data?.incidents_by_vehicle?.length || 0) === 0 ? <Empty>Aucun véhicule incidenté.</Empty> : (
                <ul className="divide-y divide-zinc-100">
                  {data.incidents_by_vehicle.map((r, i) => (
                    <li key={`${r.vehicle_id}-${i}`} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`an-inc-veh-${i}`}>
                      <Trophy size={12} className={i === 0 ? "text-amber-500" : "text-zinc-400"} />
                      <span className="font-mono truncate flex-1">{r.label}</span>
                      <span className="font-mono font-bold text-red-700">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Incidents by driver */}
            <Card title="Top chauffeurs incidentés" icon={Users} testId="an-card-inc-drv">
              {(data?.incidents_by_driver?.length || 0) === 0 ? <Empty>Aucun chauffeur incidenté.</Empty> : (
                <ul className="divide-y divide-zinc-100">
                  {data.incidents_by_driver.map((r, i) => (
                    <li key={`${r.driver_id}-${i}`} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`an-inc-drv-${i}`}>
                      <UserCheck size={12} className="text-zinc-400" />
                      <span className="truncate flex-1">{r.name}</span>
                      <span className="font-mono font-bold text-red-700">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Top delays */}
            <Card title="Top retards" icon={TrendingDown} span="lg:col-span-2" testId="an-card-top-delays">
              {(data?.top_delays?.length || 0) === 0 ? <Empty>Aucun retard ≥ 3 min sur la période.</Empty> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="text-left py-1.5">Quand</th>
                    <th className="text-left py-1.5">Ligne</th>
                    <th className="text-left py-1.5">Arrêt</th>
                    <th className="text-right py-1.5">Retard</th>
                  </tr></thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.top_delays.map((e, i) => (
                      <tr key={i} data-testid={`an-top-delay-${i}`}>
                        <td className="py-1.5 font-mono text-zinc-500">{(e.created_at || "").slice(5, 16).replace("T", " ")}</td>
                        <td className="py-1.5"><LineBadge rn={e.route_short_name || "?"} /></td>
                        <td className="py-1.5 truncate max-w-[220px]">{e.stop_name || "—"}</td>
                        <td className={`py-1.5 text-right font-mono font-bold ${(e.delay_minutes || 0) >= 7 ? "text-red-700" : "text-amber-700"}`}>+{e.delay_minutes}m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            {/* Future-AI placeholder */}
            <Card title="Assistant IA · Phase 2B" icon={Activity} testId="an-card-future">
              <div className="text-xs text-zinc-500 space-y-1.5">
                <p>L'analyse prédictive, les recommandations d'optimisation et l'assistant conversationnel arriveront ici.</p>
                <p className="font-mono text-[10px] text-zinc-400">Réservé pour la Phase 2B — pas d'IA active sur cette itération.</p>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, span = "", testId }) {
  return (
    <section className={`bg-white border border-zinc-200 shadow-[2px_2px_0_0_#0A0A0B] flex flex-col ${span}`} data-testid={testId}>
      <header className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2 bg-zinc-50">
        {Icon && <Icon size={12} className="text-zinc-600" />}
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-700 font-bold">{title}</div>
      </header>
      <div className="p-3 flex-1">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <div className="text-xs text-zinc-400 text-center py-4">{children}</div>;
}

function LineBadge({ rn }) {
  return (
    <span className="inline-block font-display font-black text-xs tracking-tighter px-1.5 py-0.5 text-white" style={{ background: lineColor(rn) }}>
      {rn}
    </span>
  );
}

function DailyBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.events));
  return (
    <div className="flex items-end gap-1 h-32" data-testid="an-daily-bars">
      {rows.map((r) => {
        const h = (r.events / max) * 100;
        const delaysH = (r.delays / max) * 100;
        const incH = (r.incidents / max) * 100;
        return (
          <div key={r.date} className="flex-1 flex flex-col items-center gap-1 group min-w-0" title={`${r.date} · ${r.events} évts · ${r.delays} retards · ${r.incidents} inc.`}>
            <div className="flex-1 w-full flex flex-col justify-end items-stretch gap-0.5">
              <div style={{ height: `${incH}%`, minHeight: r.incidents > 0 ? "2px" : "0" }} className="bg-red-600" />
              <div style={{ height: `${delaysH}%`, minHeight: r.delays > 0 ? "2px" : "0" }} className="bg-amber-500" />
              <div style={{ height: `${h - delaysH - incH}%`, minHeight: r.events > r.delays + r.incidents ? "2px" : "0" }} className="bg-emerald-500" />
            </div>
            <div className="text-[9px] font-mono text-zinc-500 truncate w-full text-center">{r.date.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}
